import { escapeRegExp } from '@openx/utils/lib/escapeRegExp.ts';
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import { TransferListItem } from '../../components/TransferListItem.tsx';
import { useTransferListBulkAdd } from '../../hooks/useTransferListBulkAdd.ts';
import { useTransferListSelectActions } from '../../hooks/useTransferListSelectActions.ts';
import { useTransferListTransferActions } from '../../hooks/useTransferListTransferActions.ts';
import { TransferList, type TransferListProps } from '../../TransferList.tsx';
import type { TransferListItemsMap } from '../../types.ts';
import { chipFiltersConfig } from './constants.ts';
import type {
  BaseSeatTransferListItem,
  SeatTransferListFilters,
  UseSeatTransferListBulkValidateParams,
  UseSeatTransferListBulkValidateReturnType,
  UseSeatTransferListFieldsReturnType,
  UseSeatTransferListItemsFetchParameters,
  UseSeatTransferListItemsFetchReturnType,
} from './types.ts';

export type SeatTransferListProps<T extends BaseSeatTransferListItem> = {
  disableSort?: boolean;
  error?: string;
  isItemBlocked?: TransferListProps<T, SeatTransferListFilters>['leftList']['isItemBlocked'];
  isSubmitting: boolean;
  localStorageKey: string;
  rightItems: TransferListItemsMap<T>;
  setRightItems: Dispatch<SetStateAction<TransferListItemsMap<T>>>;
  useBulkValidate: (
    parameters: UseSeatTransferListBulkValidateParams<T>
  ) => UseSeatTransferListBulkValidateReturnType<T>;
  useFieldsConfig: () => UseSeatTransferListFieldsReturnType<T>;
  useItemsFetch: (parameters: UseSeatTransferListItemsFetchParameters<T>) => UseSeatTransferListItemsFetchReturnType<T>;
  leftDataTest?: string;
  rightDataTest?: string;
};

export const SeatTransferList = <T extends BaseSeatTransferListItem>({
  disableSort,
  error,
  isItemBlocked,
  isSubmitting,
  localStorageKey,
  rightItems,
  setRightItems,
  useBulkValidate,
  useFieldsConfig,
  useItemsFetch,
  leftDataTest,
  rightDataTest,
}: SeatTransferListProps<T>) => {
  const { handleBulkValidate } = useBulkValidate({
    isItemBlocked: isItemBlocked,
    itemsToIgnore: rightItems,
  });

  const { fieldsConfig, isLoading: isLoadingFields } = useFieldsConfig();
  const [chipFilters, setChipFilters] = useState<SeatTransferListFilters>({ buyer_id: null, demand_partner_id: null });
  const [isSortedDescending, setIsSortedDescending] = useState<boolean>(false);
  const [leftItems, setLeftItems] = useState<TransferListItemsMap<T>>({});
  const [leftSearchPhrase, setLeftSearchPhrase] = useState<string>('');
  const [leftSelectedItems, setLeftSelectedItems] = useState<TransferListItemsMap<T>>({});
  const [rightSelectedItems, setRightSelectedItems] = useState<TransferListItemsMap<T>>({});
  const { handleBulkAdd } = useTransferListBulkAdd({ setLeftItems, setLeftSelectedItems, setRightItems });

  const { data, isFetching: isFetchingDeals } = useItemsFetch({
    chipFilters,
    isSortedDescending,
    itemsToIgnore: rightItems,
    searchPhrase: leftSearchPhrase,
  });

  const { handleLeftSelectAll, handleLeftSelectItem, handleRightSelectAll, handleRightSelectItem } =
    useTransferListSelectActions({
      isItemBlocked,
      leftItems,
      rightItems,
      setLeftSelectedItems,
      setRightSelectedItems,
    });

  const { handleLeftToRightTransfer, handleRightToLeftTransfer } = useTransferListTransferActions({
    leftSelectedItems,
    rightSelectedItems,
    setLeftItems,
    setLeftSelectedItems,
    setRightItems,
    setRightSelectedItems,
  });

  useEffect(() => {
    setLeftItems(data);
    setLeftSelectedItems({});
  }, [data]);

  return (
    <TransferList
      ListItemComponent={TransferListItem}
      error={error}
      fields={{
        config: fieldsConfig,
        localStorageKey: localStorageKey,
      }}
      isDisabled={isSubmitting}
      isLoading={isLoadingFields}
      leftList={{
        chipFilters: {
          config: chipFiltersConfig,
          onChange: setChipFilters,
          value: chipFilters,
        },
        dataTest: leftDataTest ?? 'search-result-list',
        isItemBlocked,
        isLoading: isFetchingDeals,
        isSortedDescending,
        items: leftItems,
        itemsSelected: leftSelectedItems,
        onSelectAll: handleLeftSelectAll,
        onSelectItem: handleLeftSelectItem,
        onTransfer: handleLeftToRightTransfer,
        searchBarPlaceholder: 'Search Name or Received Seat ID',
        searchPhrase: leftSearchPhrase,
        setIsSortedDescending: disableSort ? undefined : setIsSortedDescending,
        setSearchPhrase: setLeftSearchPhrase,
        title: 'Seats List',
        titleDataTest: 'search-result-list-title',
      }}
      onBulkAdd={handleBulkAdd}
      onBulkValidate={handleBulkValidate}
      rightList={{
        dataTest: rightDataTest ?? 'item-to-add-list',
        items: rightItems,
        itemsSelected: rightSelectedItems,
        onSearch: ({ name, seat_id }, searchPhrase) => {
          const regExp = new RegExp(escapeRegExp(searchPhrase), 'i');

          return regExp.test(seat_id) || (!!name && regExp.test(name));
        },
        onSelectAll: handleRightSelectAll,
        onSelectItem: handleRightSelectItem,
        onTransfer: handleRightToLeftTransfer,
        searchBarPlaceholder: 'Search Name or Received Seat ID',
        title: 'Assigned Seats List',
        titleDataTest: 'assigned-list-title',
      }}
    />
  );
};
